/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiBreadcrumbs } from '../../breadcrumbs';
export var EuiHeaderBreadcrumbs = ({
  className,
  breadcrumbs,
  ...rest
}) => {
  const classes = classNames('euiHeaderBreadcrumbs', className);
  return <EuiBreadcrumbs max={4} truncate breadcrumbs={breadcrumbs} className={classes} {...rest} />;
};
EuiHeaderBreadcrumbs.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
     * Hides left most breadcrumbs as window gets smaller
     */
  responsive: PropTypes.bool,

  /**
     * Forces all breadcrumbs to single line and
     * truncates each breadcrumb to a particular width,
     * except for the last item
     */
  truncate: PropTypes.bool,

  /**
     * Condenses the inner items past the maximum set here
     * into a single ellipses item
     */
  max: PropTypes.number,

  /**
     * Allows the hidden breadcrumbs to be shown when
     * a `max` is set and the ellipsis is clicked in responsive mode.
     */
  showMaxPopover: PropTypes.bool,

  /**
     * The array of individual breadcrumbs, takes the following props.
     * `text` (node) (required): visible label of the breadcrumb,
     * `href` or `onClick`: provide only one (last breadcrumb will not apply either),
     * `truncate` (bool): Force a max-width on the breadcrumb text
     */
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    className: PropTypes.string,
    "aria-label": PropTypes.string,
    "data-test-subj": PropTypes.string,
    text: PropTypes.node.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    truncate: PropTypes.bool
  }).isRequired).isRequired
};

try {
  // @ts-ignore
  EuiHeaderBreadcrumbs.displayName = "EuiHeaderBreadcrumbs"; // @ts-ignore

  EuiHeaderBreadcrumbs.__docgenInfo = {
    "description": "",
    "displayName": "EuiHeaderBreadcrumbs",
    "props": {
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "responsive": {
        "defaultValue": null,
        "description": "Hides left most breadcrumbs as window gets smaller",
        "name": "responsive",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "truncate": {
        "defaultValue": null,
        "description": "Forces all breadcrumbs to single line and\ntruncates each breadcrumb to a particular width,\nexcept for the last item",
        "name": "truncate",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "max": {
        "defaultValue": null,
        "description": "Condenses the inner items past the maximum set here\ninto a single ellipses item",
        "name": "max",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "showMaxPopover": {
        "defaultValue": null,
        "description": "Allows the hidden breadcrumbs to be shown when\na `max` is set and the ellipsis is clicked in responsive mode.",
        "name": "showMaxPopover",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "breadcrumbs": {
        "defaultValue": null,
        "description": "The array of individual breadcrumbs, takes the following props.\n`text` (node) (required): visible label of the breadcrumb,\n`href` or `onClick`: provide only one (last breadcrumb will not apply either),\n`truncate` (bool): Force a max-width on the breadcrumb text",
        "name": "breadcrumbs",
        "required": true,
        "type": {
          "name": "Breadcrumb[]"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/header/header_breadcrumbs/header_breadcrumbs.tsx#EuiHeaderBreadcrumbs"] = {
      docgenInfo: EuiHeaderBreadcrumbs.__docgenInfo,
      name: "EuiHeaderBreadcrumbs",
      path: "src/components/header/header_breadcrumbs/header_breadcrumbs.tsx#EuiHeaderBreadcrumbs"
    };
} catch (__react_docgen_typescript_loader_error) {}